import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type SortingState,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import {
  Plus,
  Search,
  MapPin,
  Pencil,
  Table2,
  Columns3,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useClaims } from "@/hooks/use-claims";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { formatNumber } from "@/lib/format";
import { ConfigOverrides } from "@/components/config-overrides";
import { ServiceHoursField } from "@/components/service-hours-field";
import { ExportButton } from "@/components/export-button";
import { ImportSitesDialog } from "@/components/import-sites-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { KanbanBoard } from "@/components/kanban-board";
import {
  configOptions,
  optionLabel,
  SITE_TYPE_OPTIONS,
  SITE_TYPE_VALUES,
} from "@/lib/config-options";
import { isStorable, parseWeek, type OperatingHours } from "@/lib/operating-hours";

const PAGE_SIZE = 10;

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Site {
  id: string;
  account_id: string;
  name: string;
  slug: string;
  address: string | null;
  contact_name: string | null;
  site_type: string;
  status: string;
  currency: string | null;
  timezone: string | null;
  default_weight_unit: string | null;
  waste_pct_target: number | null;
  waste_per_cover_target_g: number | null;
  /** POS2-1259 decision 14. Not an inherited config field — a plain site column, so it sits
   *  outside ConfigOverrides rather than inside it. */
  kitchen_capacity: number | null;
  operating_hours: Record<string, unknown> | null;
  default_catalogue_list_id: string | null;
  created_at: string;
}

interface CatalogueOption {
  id: string;
  name: string;
  type: string;
  item_count: number;
}

interface Account {
  id: string;
  name: string;
}

/** Built inside the component and memoised on `t`, so the messages are the reader's — a
 * module-level schema would freeze whichever locale happened to be active at import
 * (POS2-1133 pinned decision, the `makeSchema(t)` factory CP3a landed). */
function makeCreateSiteSchema(t: TFunction) {
  return z.object({
    account_id: z.string().min(1, t("validation.accountRequired")),
    name: z.string().min(1, t("validation.nameRequired")),
    address: z.string().optional(),
    contact_name: z.string().optional(),
    contact_email: z.string().email(t("validation.invalidEmail")).or(z.literal("")).optional(),
    site_type: z.enum(SITE_TYPE_VALUES).default("corporate"),
  });
}

type CreateSiteForm = z.infer<ReturnType<typeof makeCreateSiteSchema>>;

const STATUS_STYLES: Record<string, string> = {
  active: "border-transparent bg-emerald-50 text-emerald-700",
  inactive: "border-transparent bg-gray-50 text-gray-400",
};

const STATUSES = ["active", "inactive"] as const;

export default function SettingsSitesPage() {
  const { isPlatformAdmin } = useClaims();
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Site | null>(null);
  const [view, setView] = useState<"table" | "kanban">("table");
  const [sorting, setSorting] = useState<SortingState>([]);

  const accountFilter = searchParams.get("account");

  const sitesQuery = useQuery<Site[]>({
    queryKey: ["sites", accountFilter],
    queryFn: async () => {
      const url = accountFilter
        ? `${API_URL}/api/v1/sites?account_id=${accountFilter}`
        : `${API_URL}/api/v1/sites`;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const accountsQuery = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const cataloguesQuery = useQuery<CatalogueOption[]>({
    queryKey: ["catalogue-options"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists`, { headers });
      if (!res.ok) return [];
      const body = await res.json();
      return (body.data ?? []).map((l: Record<string, unknown>) => ({
        id: l.id as string,
        name: l.name as string,
        type: l.type as string,
        item_count: (l.item_count as number) ?? 0,
      }));
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });
  const catalogues = useMemo(() => cataloguesQuery.data ?? [], [cataloguesQuery.data]);

  const siteDisplayName = useCallback(
    (site: Site) => {
      const acc = accounts.find((a) => a.id === site.account_id);
      return acc ? `${acc.name}: ${site.name}` : site.name;
    },
    [accounts],
  );

  const filtered = useMemo(() => {
    if (!sitesQuery.data) return [];
    if (!search.trim()) return sitesQuery.data;
    const q = search.toLowerCase();
    return sitesQuery.data.filter(
      (s) =>
        siteDisplayName(s).toLowerCase().includes(q) ||
        s.address?.toLowerCase().includes(q) ||
        s.contact_name?.toLowerCase().includes(q),
    );
  }, [sitesQuery.data, search, siteDisplayName]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateSiteForm) => {
      const res = await fetch(`${API_URL}/api/v1/sites`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...data,
          address: data.address || null,
          contact_name: data.contact_name || null,
          contact_email: data.contact_email || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to create site");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success(t("settings.siteCreated"));
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const editMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        name: string;
        address?: string;
        contact_name?: string;
        site_type: string;
        currency?: string;
        timezone?: string;
        default_weight_unit?: string;
        waste_pct_target?: string;
        waste_per_cover_target_g?: string;
        kitchen_capacity?: string;
        operating_hours: OperatingHours | null;
        default_catalogue_list_id?: string;
      };
    }) => {
      const res = await fetch(`${API_URL}/api/v1/sites/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name: data.name,
          address: data.address || null,
          contact_name: data.contact_name || null,
          site_type: data.site_type,
          currency: data.currency || null,
          timezone: data.timezone || null,
          default_weight_unit: data.default_weight_unit || null,
          waste_pct_target: data.waste_pct_target ? Number(data.waste_pct_target) : null,
          waste_per_cover_target_g: data.waste_per_cover_target_g
            ? Number(data.waste_per_cover_target_g)
            : null,
          kitchen_capacity: data.kitchen_capacity ? Number(data.kitchen_capacity) : null,
          // `{}` rather than `null` to clear. `SiteUpdate` is dumped with `exclude_none=True`, so a
          // `null` here would be dropped and the override would survive the save — and `{}` is the
          // unset state `resolve_fields` already treats as absent, so it is the right wire value
          // either way.
          //
          // One consequence worth stating: a *site-level* value the resolver already rejects seeds
          // as inherited, so saving this dialog for any reason clears it. That is a repair rather
          // than a loss — the value was being skipped on every read — and the warning it raised
          // goes away with it. A rejected value at an ancestor level is untouched, because the
          // site's own column is already `{}` and stays that way.
          operating_hours: data.operating_hours ?? {},
          // Platform admins only (POS2-1480): the site's catalogue grounds its classification and
          // menu generation, so switching it is ours to do, as with the org's (POS2-789).
          ...(isPlatformAdmin && {
            default_catalogue_list_id: data.default_catalogue_list_id || null,
          }),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update site");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success(t("settings.siteUpdated"));
      setEditing(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await fetch(`${API_URL}/api/v1/sites/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message ?? "Failed to update status");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateSiteForm>({
    resolver: zodResolver(useMemo(() => makeCreateSiteSchema(t), [t])),
    defaultValues: { site_type: "corporate", account_id: accountFilter ?? "" },
  });

  const currentAccount = accountFilter
    ? accountsQuery.data?.find((a) => a.id === accountFilter)
    : null;

  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Site>[]>(
    () => [
      {
        id: "displayName",
        accessorFn: (row) => siteDisplayName(row),
        header: ({ column }) => (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex cursor-pointer select-none items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                column.toggleSorting(column.getIsSorted() === "asc");
            }}
          >
            {t("settings.name")}
            <ArrowUpDown className="size-3 text-muted-foreground/50" />
          </span>
        ),
        cell: ({ row }) => (
          <button
            className="text-sm font-medium hover:underline"
            onClick={() => navigate(`/settings/kitchens?site=${row.original.id}`)}
          >
            {siteDisplayName(row.original)}
          </button>
        ),
      },
      {
        accessorKey: "site_type",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {optionLabel(t, SITE_TYPE_OPTIONS, row.original.site_type)}
          </Badge>
        ),
      },
      {
        accessorKey: "address",
        header: "Address",
        cell: ({ row }) => (
          <span className="truncate text-sm text-muted-foreground">
            {row.original.address ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            className={
              STATUS_STYLES[row.original.status] ?? "border-transparent bg-gray-100 text-gray-600"
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
          </Button>
        ),
      },
    ],
    [navigate, siteDisplayName, t],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">
            {currentAccount
              ? t("settings.sitesTitleForAccount", { account: currentAccount.name })
              : t("settings.sitesTitle")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {currentAccount
              ? t("settings.sitesDescriptionForAccount")
              : t("settings.sitesDescriptionAll")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ImportSitesDialog />
          <ExportButton entity="sites" />
          <Dialog
            open={open}
            onOpenChange={(v) => {
              setOpen(v);
              if (!v) reset({ site_type: "corporate", account_id: accountFilter ?? "" });
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-1 size-4" />
                {t("settings.newSite")}
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle>{t("settings.createSite")}</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={handleSubmit((d) => createMutation.mutate(d))}
                className="flex min-h-0 flex-1 flex-col gap-4"
              >
                <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
                  <div>
                    <label className="text-sm font-medium" htmlFor="account_id">
                      {t("settings.account")}
                    </label>
                    <select
                      id="account_id"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      {...register("account_id")}
                    >
                      <option value="">{t("settings.selectAccount")}</option>
                      {accountsQuery.data?.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.name}
                        </option>
                      ))}
                    </select>
                    {errors.account_id && (
                      <p className="mt-1 text-xs text-destructive">{errors.account_id.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium" htmlFor="name">
                        {t("settings.siteName")}
                      </label>
                      <Input
                        id="name"
                        placeholder={t("settings.siteNamePlaceholder")}
                        {...register("name")}
                      />
                      {errors.name && (
                        <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium" htmlFor="site_type">
                        {t("settings.type")}
                      </label>
                      <select
                        id="site_type"
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        {...register("site_type")}
                      >
                        {configOptions(t, SITE_TYPE_OPTIONS).map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="address">
                      {t("settings.address")}
                    </label>
                    <Input id="address" {...register("address")} />
                  </div>
                  <div>
                    <label className="text-sm font-medium" htmlFor="contact_name">
                      {t("settings.contactName")}
                    </label>
                    <Input id="contact_name" {...register("contact_name")} />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!isValid || createMutation.isPending}
                >
                  {createMutation.isPending ? t("settings.creating") : t("settings.createSite")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {sitesQuery.data && sitesQuery.data.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("settings.searchSites")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-gray-200">
            <Button
              size="sm"
              variant={view === "table" ? "secondary" : "ghost"}
              className="h-8 rounded-r-none px-2.5"
              onClick={() => setView("table")}
            >
              <Table2 className="size-4" />
            </Button>
            <Button
              size="sm"
              variant={view === "kanban" ? "secondary" : "ghost"}
              className="h-8 rounded-l-none px-2.5"
              onClick={() => setView("kanban")}
            >
              <Columns3 className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {sitesQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      )}

      {sitesQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">{t("settings.sitesLoadFailed")}</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {sitesQuery.error instanceof Error
              ? sitesQuery.error.message
              : t("settings.unknownError")}
          </p>
        </div>
      )}

      {sitesQuery.data && sitesQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <MapPin className="size-10 text-gray-300" />
          <p className="text-sm">{t("settings.noSites")}</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t("settings.createFirstSite")}
          </Button>
        </div>
      )}

      {filtered.length > 0 && view === "table" && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                {t("settings.pageOf", {
                  page: table.getState().pagination.pageIndex + 1,
                  total: table.getPageCount(),
                })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {filtered.length > 0 && view === "kanban" && (
        <KanbanBoard
          statuses={STATUSES}
          statusStyles={STATUS_STYLES}
          items={filtered}
          getStatus={(s) => s.status}
          getId={(s) => s.id}
          onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
          renderCard={(site) => (
            <>
              <button
                className="text-left text-sm font-medium hover:underline"
                onClick={() => navigate(`/settings/kitchens?site=${site.id}`)}
              >
                {siteDisplayName(site)}
              </button>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {site.address ?? "No address"}
              </p>
              <div className="mt-1 flex items-center justify-between">
                <Badge variant="secondary" className="text-[10px]">
                  {optionLabel(t, SITE_TYPE_OPTIONS, site.site_type)}
                </Badge>
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6"
                  onClick={() => setEditing(site)}
                >
                  <Pencil className="size-3" />
                </Button>
              </div>
            </>
          )}
        />
      )}

      <EditSiteDialog
        site={editing}
        onClose={() => setEditing(null)}
        onSave={(data) => editing && editMutation.mutate({ id: editing.id, data })}
        isPending={editMutation.isPending}
        catalogues={catalogues}
        canSetCatalogue={isPlatformAdmin}
      />

      {search && filtered.length === 0 && sitesQuery.data && sitesQuery.data.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <p className="text-sm">{t("settings.noResultsFor", { search })}</p>
          <Button size="sm" variant="outline" onClick={() => setSearch("")}>
            {t("settings.clearSearch")}
          </Button>
        </div>
      )}
    </div>
  );
}

function EditSiteDialog({
  site,
  onClose,
  onSave,
  isPending,
  catalogues,
  canSetCatalogue,
}: {
  site: Site | null;
  onClose: () => void;
  onSave: (data: {
    name: string;
    address?: string;
    contact_name?: string;
    site_type: string;
    currency?: string;
    timezone?: string;
    default_weight_unit?: string;
    waste_pct_target?: string;
    waste_per_cover_target_g?: string;
    kitchen_capacity?: string;
    operating_hours: OperatingHours | null;
    default_catalogue_list_id?: string;
  }) => void;
  isPending: boolean;
  catalogues: CatalogueOption[];
  canSetCatalogue: boolean;
}) {
  const { locale, t } = useTranslation();
  const editSchema = z.object({
    name: z.string().min(1, t("validation.nameRequired")),
    address: z.string().optional(),
    contact_name: z.string().optional(),
    site_type: z.enum(SITE_TYPE_VALUES),
    currency: z.string().optional(),
    timezone: z.string().optional(),
    default_weight_unit: z.string().optional(),
    waste_pct_target: z
      .string()
      .optional()
      .refine((v) => !v || (Number(v) >= 0 && Number(v) <= 100), "Must be between 0 and 100"),
    waste_per_cover_target_g: z
      .string()
      .optional()
      .refine((v) => !v || (Number.isInteger(Number(v)) && Number(v) > 0), "Whole grams above 0"),
    // Matches the column's CHECK (> 0): zero is not a site that serves nobody, it is a site that
    // has not answered — which empty already says.
    kitchen_capacity: z
      .string()
      .optional()
      .refine(
        (v) => !v || (Number.isInteger(Number(v)) && Number(v) > 0),
        t("validation.wholeNumberAboveZero"),
      ),
    // `null` is inherited. No message: the editor already prints which day is wrong and why, and a
    // second sentence at form level would say less about a value that is only wrong per day.
    operating_hours: z.custom<OperatingHours | null>().refine((v) => v === null || isStorable(v)),
    default_catalogue_list_id: z.string().optional(),
  });
  type EditForm = z.infer<typeof editSchema>;

  const form = useForm<EditForm>({ resolver: zodResolver(editSchema) });

  const [prevId, setPrevId] = useState<string | null>(null);
  if (site && site.id !== prevId) {
    setPrevId(site.id);
    form.reset({
      name: site.name,
      address: site.address ?? "",
      contact_name: site.contact_name ?? "",
      site_type: site.site_type as EditForm["site_type"],
      currency: site.currency ?? "",
      timezone: site.timezone ?? "",
      default_weight_unit: site.default_weight_unit ?? "",
      waste_pct_target: site.waste_pct_target != null ? String(site.waste_pct_target) : "",
      waste_per_cover_target_g:
        site.waste_per_cover_target_g != null ? String(site.waste_per_cover_target_g) : "",
      kitchen_capacity: site.kitchen_capacity != null ? String(site.kitchen_capacity) : "",
      // A stored value the resolver rejects seeds as inherited, exactly as the resolver treats it —
      // the warning below the field names the level rather than loading a broken week to puzzle over.
      operating_hours: parseWeek(site.operating_hours),
      default_catalogue_list_id: site.default_catalogue_list_id ?? "",
    });
  }

  return (
    <Dialog open={!!site} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t("settings.editSite")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSave)} className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="-mx-6 min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium" htmlFor="edit-site-name">
                  {t("settings.siteName")}
                </label>
                <Input id="edit-site-name" {...form.register("name")} />
                {form.formState.errors.name && (
                  <p className="mt-1 text-xs text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="edit-site-type">
                  {t("settings.type")}
                </label>
                <select
                  id="edit-site-type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("site_type")}
                >
                  {configOptions(t, SITE_TYPE_OPTIONS).map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-site-address">
                {t("settings.address")}
              </label>
              <Input id="edit-site-address" {...form.register("address")} />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-site-contact">
                {t("settings.contactName")}
              </label>
              <Input id="edit-site-contact" {...form.register("contact_name")} />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-site-catalogue">
                {t("settings.defaultCatalogue")}
              </label>
              {canSetCatalogue ? (
                <select
                  id="edit-site-catalogue"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...form.register("default_catalogue_list_id")}
                >
                  <option value="">{t("settings.useOrgDefault")}</option>
                  {catalogues.map((c) => (
                    <option key={c.id} value={c.id}>
                      {t("settings.catalogueOptionItems", {
                        name: c.name,
                        count: formatNumber(locale, c.item_count),
                      })}
                    </option>
                  ))}
                </select>
              ) : (
                <p id="edit-site-catalogue" className="py-1 text-sm text-muted-foreground">
                  {catalogues.find((c) => c.id === site?.default_catalogue_list_id)?.name ??
                    t("settings.useOrgDefault")}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="kitchen_capacity">
                {t("occupancy.settings.kitchenCapacity")}
              </label>
              <Input
                id="kitchen_capacity"
                type="number"
                min={1}
                {...form.register("kitchen_capacity")}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t("occupancy.settings.kitchenCapacityHint")}
              </p>
              {form.formState.errors.kitchen_capacity && (
                <p className="mt-1 text-xs text-destructive">
                  {form.formState.errors.kitchen_capacity.message}
                </p>
              )}
            </div>

            <ConfigOverrides
              entityType="site"
              entityId={site?.id ?? null}
              currentCurrency={form.watch("currency")}
              currentTimezone={form.watch("timezone")}
              currentWeightUnit={form.watch("default_weight_unit")}
              currentWastePctTarget={form.watch("waste_pct_target")}
              currentWastePerCoverTarget={form.watch("waste_per_cover_target_g")}
              onCurrencyChange={(v) => form.setValue("currency", v)}
              onTimezoneChange={(v) => form.setValue("timezone", v)}
              onWeightUnitChange={(v) => form.setValue("default_weight_unit", v)}
              onWastePctTargetChange={(v) =>
                form.setValue("waste_pct_target", v, { shouldValidate: true })
              }
              onWastePerCoverTargetChange={(v) =>
                form.setValue("waste_per_cover_target_g", v, { shouldValidate: true })
              }
              wastePctTargetError={form.formState.errors.waste_pct_target?.message}
              wastePerCoverTargetError={form.formState.errors.waste_per_cover_target_g?.message}
            >
              <ServiceHoursField
                siteId={site?.id ?? null}
                accountId={site?.account_id ?? null}
                value={form.watch("operating_hours")}
                onChange={(v) => form.setValue("operating_hours", v, { shouldValidate: true })}
              />
            </ConfigOverrides>
          </div>
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? t("common.saving") : t("settings.saveChangesButton")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
